import React from 'react';
import classnames from 'classnames';
import 'app/assets/stylesheets/design_system/_deprecated_grid.scss';

type GridColumnProps = React.PropsWithChildren<{
  sm: 0 | 1 | 2;
  md: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  lg: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  xl: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
}>;

const GridColumn: React.FC<GridColumnProps> = ({
  children,
  sm,
  md,
  lg,
  xl,
}) => {
  const smClass = `ds2-grid-column-small-${sm === 0 ? 'hidden' : String(sm)}`;
  const mdClass = `ds2-grid-column-medium-${md === 0 ? 'hidden' : String(md)}`;
  const lgClass = `ds2-grid-column-large-${lg === 0 ? 'hidden' : String(lg)}`;
  const xlClass = `ds2-grid-column-xlarge-${xl === 0 ? 'hidden' : String(xl)}`;

  return (
    <div className={classnames(smClass, mdClass, lgClass, xlClass)}>
      {children}
    </div>
  );
};

/**
 * @deprecated Prefer using `Grid.tsx`
 *
 * Components for full-width page layouts using the 12-column grid system.
 * (Design system concept, not to be confused with the CSS Grid that implements it.)
 *
 * Provides a responsive grid system for laying out the primary content on a page.
 * The breakpoints are:
 * - Small (2 columns): 0px - 567px
 * - Medium (8 columns): 568px - 991px
 * - Large (12 columns): 992px and up
 * - XLarge (12 columns): 1200px and up
 *
 * A layout consists of one DeprecatedGrid component, which contains one or more GridColumn components
 * as direct children. For each GridColumn, you must specify the number of columns it should
 * occupy at each breakpoint, or 0 to hide the column at that breakpoint. For example, to have
 * a column be hidden in Small, span 1 column in Medium, and span 2 columns in Large and XLarge:
 *
 * <GridColumn sm={0} md={1} lg={2} xl={2} />
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { DeprecatedGrid, GridColumn } from 'js/design_system/grid/DeprecatedGrid';
 *
 * <DeprecatedGrid>
 *   <GridColumn sm={0} md={2} lg={2} xl={2}>Left column</GridColumn>
 *   <GridColumn sm={2} md={4} lg={8} xl={8}>Center column</GridColumn>
 *   <GridColumn sm={0} md={2} lg={2} xl={2}>Right column</GridColumn>
 * </DeprecatedGrid>
 * ```
 *
 */
const DeprecatedGrid: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={classnames('ds2-grid-container', className)} {...props}>
      {children}
    </div>
  );
};

export { DeprecatedGrid, GridColumn };
